/**
 * Prompt Research Page
 *
 * Experimental environment for testing different prompt variations and data configurations.
 * Allows selecting different data files and prompt templates for rapid iteration.
 *
 * Located under Thesys Demo in workspace navigation (Caravel users only).
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { CustomC1ChatWidget } from '../thesysDemo/CustomC1ChatWidget';
import { apiUrl } from '@app/const';
import { getAccessToken } from '@app/api/getAccessToken';
import { store } from '@app/redux/store';
import { carBaseQuery } from '@app/api/carBaseQuery';

interface DataFile {
  id: string;
  name: string;
}

interface PromptTemplate {
  id: string;
  name: string;
}

interface DataFilesResponse {
  data_files: DataFile[];
}

interface PromptTemplatesResponse {
  prompt_templates: PromptTemplate[];
}

export const PromptResearchPage: React.FC = () => {
  const params = useParams();
  const planResultRootId = params.planResultRootId;
  const planResultTypeId = params.planResultTypeId;

  // State for available options
  const [dataFiles, setDataFiles] = useState<DataFile[]>([]);
  const [promptTemplates, setPromptTemplates] = useState<PromptTemplate[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  // Selection state
  const [selectedDataFile, setSelectedDataFile] = useState<string>('hub_data.json');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('hub.j2');

  // Construct API URL
  const fullApiUrl = useMemo(() => {
    const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    return `${baseUrl}/api/thesys-prompt-research/chat/`;
  }, []);

  // Fetch available data files and templates on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const accessToken = await getAccessToken(store);
        const headers = {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        };

        // Fetch data files
        const dataUrl = `${apiUrl}/api/thesys-prompt-research/data/`;
        console.log('🔍 Fetching data files from:', dataUrl);
        
        const dataResponse = await fetch(dataUrl, { headers });
        console.log('📊 Data files response status:', dataResponse.status);
        
        if (dataResponse.ok) {
          const dataResult: DataFilesResponse = await dataResponse.json();
          console.log('✅ Data files received:', dataResult);
          setDataFiles(dataResult.data_files || []);
          if (dataResult.data_files?.length > 0) {
            setSelectedDataFile(dataResult.data_files[0].id);
          }
        } else {
          console.error('❌ Data files request failed:', dataResponse.status, await dataResponse.text());
        }

        // Fetch prompt templates
        const templatesUrl = `${apiUrl}/api/thesys-prompt-research/prompts/`;
        console.log('🔍 Fetching templates from:', templatesUrl);
        
        const templatesResponse = await fetch(templatesUrl, { headers });
        console.log('📊 Templates response status:', templatesResponse.status);
        
        if (templatesResponse.ok) {
          const templatesResult: PromptTemplatesResponse = await templatesResponse.json();
          console.log('✅ Templates received:', templatesResult);
          setPromptTemplates(templatesResult.prompt_templates || []);
          if (templatesResult.prompt_templates?.length > 0) {
            setSelectedTemplate(templatesResult.prompt_templates[0].id);
          }
        } else {
          console.error('❌ Templates request failed:', templatesResponse.status, await templatesResponse.text());
        }
      } catch (error) {
        console.error('💥 Failed to fetch options:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  // Intercept fetch requests to add Authorization header and custom parameters
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.toString()
          : input.url;

      // Only intercept requests to our research endpoint
      if (url.includes('/api/thesys-prompt-research/chat/')) {
        try {
          // Get access token
          const accessToken = await getAccessToken(store);

          // Parse existing body to add our parameters
          let body = init?.body;
          if (typeof body === 'string') {
            const parsedBody = JSON.parse(body);
            // Add data file and template selections
            parsedBody.dataFile = selectedDataFile;
            parsedBody.promptTemplate = selectedTemplate;
            body = JSON.stringify(parsedBody);
          }

          // Add headers
          const headers = new Headers(init?.headers);
          if (accessToken && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          }
          if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
          }

          // Make request with modified headers and body
          return originalFetch(input, {
            ...init,
            headers,
            body,
          });
        } catch (error) {
          console.error('Failed to enhance request:', error);
          return originalFetch(input, init);
        }
      }

      return originalFetch(input, init);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [selectedDataFile, selectedTemplate]);

  // Check for required parameters
  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Missing Required Parameters</Typography>
        <Typography variant="body1" color="text.secondary">
          planResultRootId and planResultTypeId are required
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with selectors */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h5" gutterBottom>
          Prompt Research Lab
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Experiment with different data and prompt combinations
        </Typography>

        {/* Selectors */}
        {isLoadingOptions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading options...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Data Source</InputLabel>
              <Select
                value={selectedDataFile}
                label="Data Source"
                onChange={(e) => setSelectedDataFile(e.target.value)}
              >
                {dataFiles.map((file) => (
                  <MenuItem key={file.id} value={file.id}>
                    {file.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Prompt Template</InputLabel>
              <Select
                value={selectedTemplate}
                label="Prompt Template"
                onChange={(e) => setSelectedTemplate(e.target.value)}
              >
                {promptTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Chat Widget */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <CustomC1ChatWidget apiUrl={fullApiUrl} />
      </Box>
    </Box>
  );
};
